/**
 * SceneManager — owns the ONE resident 3D environment for the session.
 *
 * Why this exists: building a whole GameScene (geometry + materials + shader
 * compiles + texture uploads + shadow-map render targets + static physics
 * bodies) and tearing it back down on every entry/exit is a GPU spike that
 * WKWebView dies on (the "black render" on iOS when bouncing in and out of the
 * try-dice scenes). The real game, the tutorial, and both try-dice flows ALL
 * used to do that independently.
 *
 * Instead: the environment is built ONCE, keyed by the scene document id, and
 * kept resident. Entering a game / tutorial / try-dice REUSES it (zero GPU
 * work) and only adds/removes the cheap actors (dice, betting) on top. Because
 * cee-lo and craps share one scene id, a normal session never rebuilds — the
 * scene + its lights stay warm the whole time, so no shader recompiles.
 *
 * The environment is disposed only when the scene id actually changes (a rare
 * content update from the scene editor) or on app teardown.
 *
 * NOTE: there is exactly one WebGL renderer (SharedRenderer) and one Engine.
 * SceneManager does NOT create either — it only owns what lives in engine.scene
 * / engine.physics.world.
 */

import type { Engine } from '../../engine/Engine';
import type { GameModeId } from '@sdl/scene-format';
import { sceneApi } from '../../api/sceneApi';
import { GameScene } from './GameScene';
import { CameraController } from '../camera/CameraController';
import { settingsStore } from '../../settings/SettingsStore';

export class SceneManager {
  private static _instance: SceneManager | null = null;

  private engine: Engine;
  private resident: GameScene | null = null;
  private residentSceneId: string | null = null;
  /** Serializes ensureScene calls so two builds can never run at once. */
  private chain: Promise<unknown> | null = null;
  /** The ONE camera controller, bound to the shared engine.camera. Like the
   *  renderer and the resident scene, the camera is a session-wide resource —
   *  every 3D context (game / try-dice / tutorial) drives this same instance
   *  instead of newing its own. */
  readonly cameraController: CameraController;

  private constructor(engine: Engine) {
    this.engine = engine;
    this.cameraController = new CameraController(engine.camera, {
      defaultPosition: { x: 0, y: 14, z: 10 },
      defaultLookAt: { x: 0, y: 0, z: 0 },
      lerpSpeed: 3,
      zoomLerpSpeed: 2,
      minHeight: 6,
      padding: 2.5,
      diceSize: 1,
    });
  }

  static init(engine: Engine): SceneManager {
    if (!SceneManager._instance) {
      SceneManager._instance = new SceneManager(engine);
    }
    return SceneManager._instance;
  }

  static get instance(): SceneManager {
    if (!SceneManager._instance) {
      throw new Error('SceneManager not initialized. Call SceneManager.init(engine) first.');
    }
    return SceneManager._instance;
  }

  /** The resident scene, or null if none built yet. */
  get current(): GameScene | null {
    return this.resident;
  }

  /**
   * Ensure the environment for `gameModeId` is resident and return it.
   * Reuses the resident scene when the scene id is unchanged; otherwise disposes
   * the old one and builds the new one. Concurrent calls are serialized and
   * collapse onto the same resident scene (so a double-tap can't build twice).
   */
  async ensureScene(gameModeId: GameModeId): Promise<GameScene> {
    const next = (this.chain ?? Promise.resolve()).then(
      () => this._ensure(gameModeId),
      () => this._ensure(gameModeId),
    );
    // Keep the chain alive regardless of this call's success/failure.
    this.chain = next.then(() => undefined, () => undefined);
    return next;
  }

  private async _ensure(gameModeId: GameModeId): Promise<GameScene> {
    const doc = await sceneApi.getForGameMode(gameModeId);
    const sceneId = doc.metadata?.id ?? null;

    // Same scene already resident → reuse, no GPU work.
    if (this.resident && sceneId && this.residentSceneId === sceneId) {
      return this.resident;
    }

    // Scene id changed (or first build): dispose the old environment fully
    // (geometry, materials, textures, shadow maps, static bodies) before
    // building the new one. This path is rare — a content update, not gameplay.
    if (this.resident) {
      this.resident.dispose();
      this.resident = null;
      this.residentSceneId = null;
    }

    const scene = new GameScene(this.engine, { gameModeId });
    await scene.initialize(doc);
    this.resident = scene;
    this.residentSceneId = sceneId;

    // Re-apply the quality tier to the freshly loaded lights NOW, so the shadow
    // setup (map size, spot focus, softening) lands for EVERY consumer of the
    // resident scene — not just the game-entry path that used to call this. The
    // try-dice / tutorial previews reuse this scene without entering a game, so
    // without this their shadows kept the raw SceneLoader defaults.
    this.engine.setQuality(settingsStore.get('graphics').quality);

    return scene;
  }

  /**
   * Warm the environment in the background (fire-and-forget). Safe to call when
   * you expect the player to enter 3D soon; errors are swallowed (the next real
   * ensureScene will surface them). Not called eagerly today to avoid holding
   * environment VRAM before the first 3D use.
   */
  prefetch(gameModeId: GameModeId): void {
    this.ensureScene(gameModeId).catch(() => { /* the real entry handles failures */ });
  }

  /** Full teardown (app shutdown / logout). */
  dispose(): void {
    this.resident?.dispose();
    this.resident = null;
    this.residentSceneId = null;
    this.chain = null;
  }
}
